/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a step, event, header, method or storage code; it renders nothing */
// The offline runtime for the web app: boots C3 recovery, C2 identity, C1
// connectivity and the C1b scheduler, keeps this tab's owner context, and
// owns the transports and the public calls the UI uses (record a deck edit,
// queue a collection add, store a deck snapshot, read what is stored).
//
// Active only with IndexedDB and once started (main.tsx, with the app's
// auth adapter). Without it, every caller falls back to today's online-only
// behavior, so tests and browsers without IndexedDB are unchanged.
//
// No browser global at module scope.
import { CONN, CONN_EVENT, FENCE_MIRROR_KEY, GUEST_OWNER, IDENTITY_EMIT, META_DECKS, MODE, OP_STATE, SCHEMA_VERSION, STORE, TRIGGER, type C3Reason } from './offline.codes'
import {
  activateOwnerDb, fencedWrite, hasIndexedDb, incarnationOf, readDeviceIndex, readFenceMirror, readStorageFence, updateDeviceIndex, writeFenceMirror,
  type WriterCtx,
} from './offlineStore'
import { beginWriter, currentWriterCtx, knownWriterCtx, adoptFenceEpoch, setSuspended, setWriterCtx, subscribeOfflineContext } from './offlineContext'
import { recoverOnLoad, runTransition, waitForTransitionLock, type TransitionDeps, type TransitionOutcome, type TransitionRequest } from './transition'
import { actionForMessage, fenceActionFor, type FenceAction } from './peerFence'
import { connectivityState, onConnectivityEvent, onForeground, startConnectivity } from './connectivity'
import {
  getIdentity, identityClearRejection, identityOwnerChanged, identityRefreshTerminal, identityRefreshTransient, identitySessionChanged,
  onIdentityEmit, setIdentityFromIndex, setIdentityUnavailable, subscribeIdentity,
} from './identity'
import { accountOwner, isAccountOwner, isTerminalRefresh, ownerSubject } from './identityMachine'
import { configureScheduler, requestSync, armTick } from './syncScheduler'
import {
  addAgainAsNew, approveSend, buildCollectionAddRecords, decideHead, drainCollectionQueue, isOpen, makeQueueDue, putRecords, readQueue, recoverQueueAtLaunch,
  requestText, retryFailedAsNew, reviewOptions,
  type AddItemWire, type DispatchCurrent, type OpRecord, type Reconciliation,
} from './opQueue'
import {
  discardDeckDraft, drainDeckEdits, keepMineDeckDraft, makeDeckAttemptsDue, readDrafts, recordDeckEdit, retryDeckDraft,
  type DeckDraft, type DeckTransport, type EditInput,
} from './deckOutbox'
import { exchangeForThrow, detailOf, retryAfterSeconds, type HttpExchange } from './httpOutcome'
import {
  cardKeysOf, readDeckCardAges, readDeckCards, readDeckSnapshot, refreshCollectionSnapshot, writeDeckCards, writeDeckSnapshot, writeProfile, readProfile,
  type StoredProfile,
} from './snapshots'
import { setOfflineCardSource } from './cardSource'
import { isOwnMessage } from '../accountChannel'
import { activateOwnerStorage, ownerStorage, resetOwnerStorage, setOwnerWriteGuard, startOwnerStorageRouting } from './ownerStorage'
import { setCollectionRecordReader } from '../collectionScope'
import { bumpSessionGeneration, readStoredRefresh, refreshAnswerCurrent, sessionGeneration, setIncarnationReader, setRefreshObserver } from './refreshObserver'
import { setCollectionWriteListener } from './collectionSignal'
import { rfc3339 } from './time'
import type { SavedDeck } from '../../hooks/useSavedDecks'
import { cardKey, type CardImage } from '../../api/cardImages'
import { OFFLINE_IMAGES } from '../flags'
import { imagesPendingSync, primeImagePending, runImageStep } from './images'
import { firstPageFor, readFirstPageRecord, type FirstPageFence, type ProvisionalFirstPage } from './firstPage'
import { DEFAULT_SORT, readSort } from '../collectionSortPref'
import type { CopySort, SortDir } from '../../api/collectionCopies.codes'

// ---- The app adapter (registered by main.tsx; useAuth reads it) ----------------------------------

export interface OfflineAuthAdapter {
  /** The stored session WITHOUT the network: subject + expiry (s), null none, 'unknown' unreadable. */
  storedSession: () => Promise<{ subject: string | null; expiresAt: number | null; anonymous: boolean } | null | 'unknown'>
  /** C3 step 6 for `ownerKey`: local-scope sign-out, verified. True when storage no longer holds it. */
  localSignOut: (ownerKey: string) => Promise<boolean>
  /** Owner-derived state outside IndexedDB (localStorage keys, caches). */
  wipeExtra: (ownerKey: string, reason: C3Reason) => Promise<void>
  /** Deck ids with unsynced editor drafts outside IndexedDB. */
  extraUnsyncedDeckIds: (ownerKey: string) => string[]
  /** Step 0: one single-flight refresh, classified. */
  refreshSession: () => Promise<{ ok: true; subject: string; expiresAt: number | null } | { ok: false; status: number; code: string | null; missing: boolean }>
  /** The live session as {userId, token} (no refresh), null unknown. */
  sessionIdentity: () => Promise<{ userId: string | null; token: string | null } | null>
  /** Supabase REST base and anon key (null: not configured). */
  supabaseRest: () => { url: string; anonKey: string } | null
  /** The Supabase transport (fencedFetch). */
  supabaseFetch: typeof fetch
  /** apiFetch (reports to C1). */
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>
  apiUrl: (path: string) => string
  /** The owner's current collection id (its account scope pointer), or null. */
  collectionIdFor: (userId: string) => string | null
  /** /cards/images for names (chunked). */
  fetchCardInfo: (names: string[]) => Promise<CardImage[]>
  /** Names of every card in a decklist. */
  cardNamesOf: (decklist: string) => string[]
  /** C3 owner_fence / owner_fence_cancelled / owner_changed (BroadcastChannel cb-account + the W1 marker). */
  broadcast: (msg: Record<string, unknown>) => void
  /** A peer's transition took this tab's owner away. */
  reload: () => void
  /** The C3 confirmation ("N changes are not saved yet and will be removed"). */
  confirm: (count: number, reason: C3Reason) => Promise<boolean>
}

let adapter: OfflineAuthAdapter | null = null
let bootPromise: Promise<void> | null = null
let active = false
const teardown: Array<() => void> = []

export function offlineRuntimeActive(): boolean {
  return active && !!adapter
}

/** start() was called (boot may still be running; offlineReady() waits for it). */
export function offlineRuntimeStarted(): boolean {
  return bootPromise !== null
}

export function getOfflineAdapter(): OfflineAuthAdapter | null {
  return adapter
}

/** Resolves once boot (recovery, identity) finished; at once when never started. */
export function offlineReady(): Promise<void> {
  return bootPromise ?? Promise.resolve()
}

// ---- Pending work (the offline bar, C3 count, the scheduler's tick) ---------------------------------

export interface PendingWork {
  /** Queue records not done / failed, plus deck drafts. */
  unsynced: number
  /** Records waiting for the user (25-day review, a conflict, a failed item). */
  attention: number
  /** Collection adds queued (not yet landed). */
  queuedAdds: number
  /** Deck drafts not yet landed. */
  deckDrafts: number
}

let pending: PendingWork = { unsynced: 0, attention: 0, queuedAdds: 0, deckDrafts: 0 }
const pendingListeners = new Set<() => void>()
// Decks whose edit the outbox holds (a draft or an unsettled attempt): one
// writer per deck, so the editor records instead of sending (sync read).
let decksWithOfflineWork = new Set<string>()

export function deckHasOfflineWorkSync(deckId: string): boolean {
  return decksWithOfflineWork.has(deckId)
}
let snapshotsDue = { decks: false, deckCards: true, collection: true }
let lastCollectionRefresh = 0

export function getPendingWork(): PendingWork {
  return pending
}

export function subscribePendingWork(fn: () => void): () => void {
  pendingListeners.add(fn)
  return () => { pendingListeners.delete(fn) }
}

export async function refreshPendingCounts(): Promise<PendingWork> {
  const ctx = knownWriterCtx()
  if (!ctx || !active) {
    pending = { unsynced: 0, attention: 0, queuedAdds: 0, deckDrafts: 0 }
    decksWithOfflineWork = new Set()
  } else {
    const q = await readQueue(ctx.ownerKey)
    const drafts = await readDrafts(ctx.ownerKey)
    const open = q.filter(isOpen)
    decksWithOfflineWork = new Set([...drafts.map(d => d.deck_id), ...open.filter(r => r.kind === 'deck_edit').map(r => r.scope.id)])
    const attention = q.filter(r => r.state === OP_STATE.failedPermanent || (r.state === OP_STATE.done && !!r.receipt) || r.hold?.reason === 'needs_review' || r.hold?.reason === 'conflict').length
      + drafts.filter(d => d.status).length
    pending = {
      unsynced: open.length + drafts.length,
      attention,
      queuedAdds: open.filter(r => r.kind === 'collection_add').length,
      deckDrafts: drafts.length,
    }
  }
  for (const l of [...pendingListeners]) l()
  return pending
}

function hasPendingWorkSync(): boolean {
  // Only an account owner has owner work (a guest's tick would spin forever).
  if (!knownWriterCtx()) return false
  return pending.unsynced > 0 || snapshotsDue.collection || snapshotsDue.deckCards || (OFFLINE_IMAGES && imagesPendingSync())
}

// ---- Boot -----------------------------------------------------------------------------------------

// ---- Blocked: a committed transition could not finish (findings 11-13) -------------------------

let blocked = false
const blockedListeners = new Set<() => void>()
let recoveryTimer: ReturnType<typeof setInterval> | null = null

/** A tombstone is pending: no owner reads, no adoption, no account steps until recovery completes it. */
export function offlineRecoveryBlocked(): boolean {
  return blocked
}

export function subscribeRecoveryBlocked(fn: () => void): () => void {
  blockedListeners.add(fn)
  return () => { blockedListeners.delete(fn) }
}

function enterBlocked(): void {
  if (!blocked) {
    blocked = true
    for (const l of [...blockedListeners]) l()
  }
  setWriterCtx(null)
  resetOwnerStorage()
  setOfflineCardSource(null)
  identityOwnerChanged(null, false)
  identitySessionChanged(null, null)
  if (recoveryTimer || typeof setInterval === 'undefined') return
  // Retried until both the session removal and the wipe are verified; then
  // a fresh load starts from the clean device.
  recoveryTimer = setInterval(() => { void retryRecovery() }, 30_000)
}

let recovering: Promise<boolean> | null = null

/** One more recovery attempt (foreground, the timer). True when nothing is pending any more. */
export function retryRecovery(): Promise<boolean> {
  if (!blocked || !adapter) return Promise.resolve(!blocked)
  recovering ??= recoverOnLoad(transitionDeps()).then((rec) => {
    if (rec.status !== 'ok') return false
    if (recoveryTimer) clearInterval(recoveryTimer)
    recoveryTimer = null
    adapter?.reload()
    return true
  }, () => false).finally(() => { recovering = null })
  return recovering
}

function transitionDeps(confirm?: TransitionDeps['confirm'], reason: C3Reason = 'sign_out'): TransitionDeps {
  const a = adapter!
  return {
    confirm: confirm ?? a.confirm,
    authSignOut: owner => a.localSignOut(owner),
    // 'unknown' stays unknown (r2 #12): recovery then signs the tombstoned
    // owner out and requires a VERIFIED removal; only a positively absent
    // session (null) or another account's is "gone".
    storedSessionOwner: async () => {
      let s: Awaited<ReturnType<OfflineAuthAdapter['storedSession']>>
      try {
        s = await a.storedSession()
      } catch {
        return 'unknown'
      }
      if (s === 'unknown') return 'unknown'
      return s && s.subject && !s.anonymous ? accountOwner(s.subject) : null
    },
    // The owner's in-memory records go first (their fenced store is wiped with the database).
    wipeExtra: async owner => { resetOwnerStorage(); await a.wipeExtra(owner, reason) },
    extraUnsyncedDeckIds: owner => a.extraUnsyncedDeckIds(owner),
    broadcast: msg => a.broadcast(msg),
    onCancelled: () => requestSync(TRIGGER.enqueued),
  }
}

/**
 * Start the runtime (idempotent). Order: C3 recovery (before any UI reads
 * owner data), the device index into C2, this tab's owner context, launch
 * recovery of the queue, then C1, the scheduler and the peer listeners, then
 * app_start.
 */
export function startOfflineRuntime(a: OfflineAuthAdapter): Promise<void> {
  if (bootPromise) return bootPromise
  adapter = a
  // A load that enters on /collection holds the A1 snapshot refresh from
  // the start, before boot can schedule the startup sync: the page's first
  // list read goes first. The page releases it (CollectionPage), else it
  // goes after SNAPSHOT_HOLD_MAX_MS.
  if (enteredOnCollection() && !entryHoldRelease) entryHoldRelease = takeHold()
  bootPromise = boot().catch((e) => {
    console.error('Offline runtime failed to start', e)
    active = false
    setIdentityUnavailable()
  })
  return bootPromise
}

async function boot(): Promise<void> {
  if (!hasIndexedDb()) {
    setIdentityUnavailable()
    return
  }
  // From here the owner's records are never localStorage's (finding 1).
  startOwnerStorageRouting()
  // C2 for every owner record (r2 #2): no change while read-only or blocked.
  setOwnerWriteGuard(() => !blocked && getIdentity().allowed.edit)
  setCollectionRecordReader(k => ownerStorage.getItem(k))
  let rec = await recoverOnLoad(transitionDeps())
  for (let i = 0; i < 5 && rec.status === 'suspended'; i++) {
    // Another tab's transition is live (its dialog is open): wait for it.
    setSuspended(true)
    await waitForTransitionLock()
    rec = await recoverOnLoad(transitionDeps())
  }
  setSuspended(false)
  if (rec.status === 'unresolved') {
    // A committed sign-out / switch could not finish: fail closed.
    active = true
    setIdentityFromIndex({ ...rec.index, offline_owner: null })
    wire()
    enterBlocked()
    return
  }
  if (rec.status === 'suspended') {
    // Still another tab's live transition after waiting: start suspended, read nothing.
    active = true
    setSuspended(true)
    setIdentityFromIndex({ ...(await readDeviceIndex()), offline_owner: null })
    wire()
    return
  }
  const idx = rec.index
  active = true
  await adoptOwner(idx.offline_owner, incarnationOf(idx, idx.offline_owner ?? ''))
  setIdentityFromIndex(idx)
  // C2 cold start: the stored session (no network).
  const stored = await adapter!.storedSession()
  if (stored && stored !== 'unknown' && stored.subject && !stored.anonymous) identitySessionChanged(stored.subject, stored.expiresAt)
  if (isAccountOwner(idx.offline_owner) && getIdentity().mode === MODE.refreshPending) identityRefreshTransient()
  const ctx = knownWriterCtx()
  if (ctx) await recoverQueueAtLaunch(ctx)
  await loadOfflineCardSource()
  // Pinned images still missing (W6) are pending work from the durable manifest.
  if (OFFLINE_IMAGES) await primeImagePending().catch(() => false)
  wire()
  await refreshPendingCounts()
  requestSync(TRIGGER.appStart)
  armTick()
}

/** This tab's owner context for `owner` (creating its database on first use). */
async function adoptOwner(owner: string | null, incarnation: number): Promise<void> {
  if (!isAccountOwner(owner) || blocked) {
    setWriterCtx(null)
    resetOwnerStorage()
    return
  }
  // Never an owner with a pending tombstone (its fence stays closed).
  const idxNow = await readDeviceIndex()
  if (idxNow.pending_wipes.some(t => t.owner_key === owner)) {
    enterBlocked()
    return
  }
  let fence = await readStorageFence(owner)
  if (!fence || fence.owner_incarnation !== incarnation || fence.state !== 'open') {
    // First load after this shipped, or a database the orphan sweep lost:
    // provision it for the owner the device index names.
    const epoch = Math.max(fence?.fence_epoch ?? 0, readFenceMirror()?.fence_epoch ?? 0) + 1
    await activateOwnerDb(owner, incarnation, epoch)
    fence = { owner_key: owner, owner_incarnation: incarnation, fence_epoch: epoch, state: 'open' }
  }
  const ctx = { ownerKey: owner, incarnation, fenceEpoch: fence.fence_epoch }
  setWriterCtx(ctx)
  const m = readFenceMirror()
  if (!m || m.owner_key !== owner || m.fence_epoch !== fence.fence_epoch || m.transition_id) {
    writeFenceMirror({ owner_key: owner, fence_epoch: fence.fence_epoch, transition_id: null })
  }
  // The owner's former localStorage records: migrated once, then read from memory.
  await activateOwnerStorage(ctx)
}

let writeSignalTimer: ReturnType<typeof setTimeout> | null = null

function wire(): void {
  startConnectivity()
  // What GoTrue actually answered a refresh (finding 14): only the
  // contract's terminal codes reject; anything else is transient.
  // Answers that arrived before this (from client init on) are delivered
  // now; each applies only while it still speaks for the owner's stored
  // session (r2 #5-6).
  setIncarnationReader(() => knownWriterCtx()?.incarnation ?? null)
  teardown.push(() => setIncarnationReader(null))
  setRefreshObserver((ans) => {
    if (ans.status >= 200 && ans.status < 300) return
    if (!isTerminalRefresh(ans.status, ans.code)) {
      identityRefreshTransient()
      return
    }
    if (refreshAnswerCurrent(ans, ownerSubject(getIdentity().offlineOwner ?? ''))) void identityRefreshTerminal()
  })
  teardown.push(() => setRefreshObserver(null))
  // A per-copy write landed online: refresh the offline copy soon (coalesced;
  // the snapshot route allows 12 a minute).
  setCollectionWriteListener(() => {
    snapshotsDue = { ...snapshotsDue, collection: true }
    lastCollectionRefresh = 0
    if (writeSignalTimer) return
    writeSignalTimer = setTimeout(() => { writeSignalTimer = null; requestSync(TRIGGER.enqueued) }, 5000)
  })
  teardown.push(() => {
    setCollectionWriteListener(null)
    if (writeSignalTimer) clearTimeout(writeSignalTimer)
    writeSignalTimer = null
  })
  // The installed app keeps its offline data through storage pressure (C5 rule 4).
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    void navigator.storage?.persist?.().catch(() => false)
  }
  configureScheduler({
    connectivity: connectivityState,
    mode: () => getIdentity().mode,
    hasPendingWork: hasPendingWorkSync,
    refreshSession,
    makeHeadsDue,
    now: () => Date.now(),
    jitter: () => Math.random(),
  }, [
    { name: 'collection_queue', kind: 'account', run: drainCollection },
    { name: 'deck_outbox', kind: 'account', run: drainDecks },
    { name: 'collection_snapshot', kind: 'account', run: refreshCollection },
    { name: 'deck_cards', kind: 'account', run: refreshDeckCardsStep },
    // W6 owner images: the LAST step (C1b run order 4), lowest priority.
    ...(OFFLINE_IMAGES ? [{ name: 'images', kind: 'account' as const, run: () => runImageStep() }] : []),
  ])
  teardown.push(onConnectivityEvent(e => {
    if (e === CONN_EVENT.recovered) {
      snapshotsDue = { ...snapshotsDue, collection: true }
      requestSync(TRIGGER.recovered)
    }
  }))
  teardown.push(onForeground(() => {
    if (blocked) { void retryRecovery(); return }
    checkMirror()
    requestSync(TRIGGER.foreground)
  }))
  teardown.push(onIdentityEmit(e => {
    if (e === IDENTITY_EMIT.sessionRestored) requestSync(TRIGGER.sessionRestored)
    if (e === IDENTITY_EMIT.requestRefresh) requestSync(TRIGGER.tick)
  }))
  if (typeof BroadcastChannel !== 'undefined') {
    const ch = new BroadcastChannel('cb-account')
    ch.onmessage = (ev: MessageEvent) => {
      // This tab's own transition messages come back to its other channel objects.
      if (isOwnMessage(ev.data) || transitionInFlight) return
      applyFenceAction(actionForMessage(knownWriterCtx()?.ownerKey ?? null, ev.data))
    }
    teardown.push(() => ch.close())
  }
  if (typeof window !== 'undefined') {
    const onShow = () => checkMirror()
    window.addEventListener('pageshow', onShow)
    document.addEventListener('resume', onShow)
    teardown.push(() => window.removeEventListener('pageshow', onShow), () => document.removeEventListener('resume', onShow))
  }
}

/** pageshow / visibility / resume: a frozen tab that missed a message is stopped here. */
function checkMirror(): void {
  const ctx = knownWriterCtx()
  applyFenceAction(fenceActionFor(ctx ? { ownerKey: ctx.ownerKey, fenceEpoch: ctx.fenceEpoch } : null, readFenceMirror()))
}

function applyFenceAction(a: FenceAction): void {
  switch (a.action) {
    case 'suspend':
      setSuspended(true)
      break
    case 'adopt': {
      // Only what the mirror says NOW lifts the suspension (note 11): the
      // same owner, no transition, exactly this epoch.
      const m = readFenceMirror()
      const ctx = knownWriterCtx()
      if (!m || m.transition_id || !ctx || m.owner_key !== ctx.ownerKey || m.fence_epoch !== a.epoch) {
        if (m?.transition_id) setSuspended(true)
        return
      }
      adoptFenceEpoch(a.epoch)
      setSuspended(false)
      requestSync(TRIGGER.enqueued)
      break
    }
    case 'reload':
      adapter?.reload()
      break
  }
}

// ---- Owner activation and transitions (useAuth) ------------------------------------------------------

export type OwnerSessionResult = 'ok' | 'switch_needed' | 'inactive' | 'blocked'

/**
 * A live session for a PERMANENT account appeared (sign-in, restore,
 * refresh). guest -> account activates the account as the offline owner
 * (the existing guest -> account migration runs unchanged); the same owner
 * clears a terminal rejection (no C3); another account needs C3 FIRST
 * ('switch_needed'). The profile the header shows offline is stored.
 */
export async function ownerSessionSeen(userId: string, profile: Omit<StoredProfile, 'schema_version'>, expiresAt: number | null): Promise<OwnerSessionResult> {
  if (!offlineRuntimeActive()) return 'inactive'
  await offlineReady()
  if (blocked) return 'blocked'
  const incoming = accountOwner(userId)
  const idx = await readDeviceIndex()
  if (idx.pending_wipes.length > 0) {
    enterBlocked()
    return 'blocked'
  }
  if (isAccountOwner(idx.offline_owner) && idx.offline_owner !== incoming) return 'switch_needed'
  if (idx.offline_owner !== incoming) {
    const next = await updateDeviceIndex(i => { i.offline_owner = incoming; i.terminal_rejection = false })
    await adoptOwner(incoming, incarnationOf(next, incoming))
    identityOwnerChanged(incoming, false)
  } else {
    if (idx.terminal_rejection) await identityClearRejection()
    if (!knownWriterCtx()) await adoptOwner(incoming, incarnationOf(idx, incoming))
  }
  identitySessionChanged(userId, expiresAt)
  // A live session of the owner was adopted: refresh answers about an older
  // one no longer speak for it (r3 #6).
  bumpSessionGeneration()
  const ctx = currentWriterCtx()
  if (ctx) void writeProfile(ctx, profile).catch(() => {})
  await loadOfflineCardSource()
  void refreshPendingCounts()
  return 'ok'
}

/** The stored profile of offline_owner (the header and the decks list offline), or null. */
export async function offlineOwnerProfile(): Promise<StoredProfile | null> {
  if (!offlineRuntimeActive()) return null
  await offlineReady()
  const owner = getIdentity().offlineOwner
  if (!isAccountOwner(owner)) return null
  return readProfile(owner)
}

let transitionInFlight = false

/** True while this tab runs C3 (a SIGNED_OUT it causes is not a rejection). */
export function isTransitionInFlight(): boolean {
  return transitionInFlight
}

/**
 * Run C3 for the current account owner. On completion the device index
 * names the next owner and C2 follows it.
 */
export async function runAccountTransition(
  req: Omit<TransitionRequest, 'ownerKey'> & { ownerKey?: string },
  extra: Partial<TransitionDeps> = {},
): Promise<TransitionOutcome | { outcome: 'inactive' }> {
  if (!offlineRuntimeActive()) return { outcome: 'inactive' }
  await offlineReady()
  const owner = req.ownerKey ?? getIdentity().offlineOwner
  if (!isAccountOwner(owner)) return { outcome: 'noop' }
  if (blocked) return { outcome: 'noop' }
  transitionInFlight = true
  try {
    const base = transitionDeps(undefined, req.reason)
    const deps: TransitionDeps = { ...base, ...extra }
    if (extra.wipeExtra) {
      const w = extra.wipeExtra
      deps.wipeExtra = async (o: string) => { resetOwnerStorage(); await w(o) }
    }
    let out: TransitionOutcome
    try {
      out = await runTransition({ reason: req.reason, ownerKey: owner, incoming: req.incoming ?? null }, deps)
    } catch (e) {
      // Fail closed (finding 16): nothing is adopted, nothing of the owner is read.
      enterBlocked()
      throw e
    }
    if (out.outcome === 'incomplete') {
      bumpSessionGeneration()
      enterBlocked()
      await refreshPendingCounts()
      return out
    }
    const idx = await readDeviceIndex()
    // The owner departed (or tried to): older refresh answers are obsolete.
    if (out.outcome === 'done') bumpSessionGeneration()
    if (out.outcome === 'done') {
      setOfflineCardSource(null)
      resetOwnerStorage()
      identityOwnerChanged(idx.offline_owner, idx.terminal_rejection)
      identitySessionChanged(null, null)
      if (isAccountOwner(idx.offline_owner)) await adoptOwner(idx.offline_owner, incarnationOf(idx, idx.offline_owner))
    }
    await refreshPendingCounts()
    return out
  } finally {
    transitionInFlight = false
  }
}

/**
 * A SIGNED_OUT this tab did not cause. Never itself a rejection (finding
 * 14): only GoTrue's answer to the refresh decides that (the refresh
 * observer). 'owner_kept': the account still owns the device data (shown,
 * C2 mode from the facts); 'owner_moved': a transition elsewhere moved it.
 */
export async function sessionLostWithoutSignOut(previousUserId: string): Promise<'owner_kept' | 'owner_moved' | 'inactive'> {
  if (!offlineRuntimeActive()) return 'inactive'
  await offlineReady()
  if (blocked) return 'owner_moved'
  const idx = await readDeviceIndex()
  if (idx.transition || idx.offline_owner !== accountOwner(previousUserId)) return 'owner_moved'
  if (!idx.terminal_rejection && !getIdentity().terminalRejection) identityRefreshTransient()
  return 'owner_kept'
}

// ---- Step 0: the session refresh ---------------------------------------------------------------

async function refreshSession(): Promise<'ok' | 'transient' | 'terminal'> {
  const a = adapter
  if (!a) return 'transient'
  // Bound like the observer (r2 #6): the token and account this refresh
  // was about, captured before it leaves.
  const before = readStoredRefresh()
  const gen = sessionGeneration()
  const incarnation = knownWriterCtx()?.incarnation ?? null
  const binding = before && before !== 'unknown'
    ? { owner: before.subject, token: before.refreshToken, gen, incarnation }
    : { owner: null, token: null, gen: -1, incarnation }
  try {
    const r = await a.refreshSession()
    if (r.ok) {
      const owner = getIdentity().offlineOwner
      // A token for someone else never becomes the owner's session.
      if (owner !== accountOwner(r.subject)) {
        identityRefreshTransient()
        return 'transient'
      }
      identitySessionChanged(r.subject, r.expiresAt)
      return 'ok'
    }
    // A missing session is not a terminal answer (only the allowlist is).
    if (isTerminalRefresh(r.status, r.code)) {
      // Only while it still speaks for the owner's stored session.
      if (refreshAnswerCurrent(binding, ownerSubject(getIdentity().offlineOwner ?? ''))) await identityRefreshTerminal()
      return 'terminal'
    }
    identityRefreshTransient()
    return 'transient'
  } catch {
    identityRefreshTransient()
    return 'transient'
  }
}

async function makeHeadsDue(): Promise<void> {
  const ctx = currentWriterCtx()
  if (!ctx) return
  await makeQueueDue(ctx)
  await makeDeckAttemptsDue(ctx)
}

// ---- Transports -----------------------------------------------------------------------------------

/**
 * The dispatch facts NOW, read synchronously at every boundary (finding 9):
 * C2 mode and session subject, this tab's live owner context against the
 * captured one, the C3 mirror, C1, the owner's current collection.
 */
function currentNow(ctx: WriterCtx): DispatchCurrent {
  const id = getIdentity()
  const live = currentWriterCtx()
  const mirror = readFenceMirror()
  const subject = ownerSubject(ctx.ownerKey)
  const collectionId = subject && adapter ? adapter.collectionIdFor(subject) : null
  return {
    offlineOwner: id.offlineOwner,
    ownerIncarnation: live && live.ownerKey === ctx.ownerKey ? live.incarnation : -1,
    sessionSubject: id.sessionSubject,
    online: connectivityState() === CONN.online,
    mode: id.mode,
    gateOpen: !blocked && !!live && live.ownerKey === ctx.ownerKey && live.incarnation === ctx.incarnation && live.fenceEpoch === ctx.fenceEpoch
      && !!mirror && mirror.transition_id === null && mirror.owner_key === ctx.ownerKey && mirror.fence_epoch === ctx.fenceEpoch,
    collectionIds: collectionId ? [collectionId] : [],
  }
}

/** The record may still be sent under `ctx` right now (re-checked after every await). */
function sendable(ctx: WriterCtx, r: OpRecord): boolean {
  const d = decideHead({ ...r, state: OP_STATE.pending, next_attempt_at: null, hold: null }, currentNow(ctx), Date.now())
  return d.decision === 'dispatch'
}

/**
 * A request under `ctx` may leave NOW (r2 #9): the record's full dispatch
 * decision, or for a row read the same facts (C3 fence and epoch, owner and
 * incarnation, C2 active with the owner's session, C1 online). Called
 * synchronously right before every fetch, after the last await.
 */
function liveFor(ctx: WriterCtx, r: OpRecord | null): boolean {
  if (r) return sendable(ctx, r)
  const cur = currentNow(ctx)
  return cur.gateOpen && cur.online && cur.mode === MODE.active && cur.offlineOwner === ctx.ownerKey
    && cur.ownerIncarnation === ctx.incarnation && !!cur.sessionSubject && accountOwner(cur.sessionSubject) === ctx.ownerKey
}

/** The bearer of the owner's live session, re-checked against the captured context after the await. */
async function ownerBearer(ctx: WriterCtx, r: OpRecord | null): Promise<string | null> {
  const ident = await adapter!.sessionIdentity()
  if (!ident?.userId || !ident.token || accountOwner(ident.userId) !== ctx.ownerKey) return null
  if (!liveFor(ctx, r)) return null
  return ident.token
}

async function answeredFrom(res: Response): Promise<{ exchange: HttpExchange; body: unknown }> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  const detail = res.ok ? null : detailOf(body)
  const code = !res.ok && body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string' ? (body as { code: string }).code : null
  return {
    exchange: { kind: 'answered', status: res.status, detail, code, routeFamily: 'per_copy', retryAfterS: retryAfterSeconds(res.headers.get('Retry-After')) },
    body,
  }
}

function onLine(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

async function sendApiRecord(ctx: WriterCtx, r: OpRecord): Promise<{ exchange: HttpExchange; body: unknown }> {
  const a = adapter!
  // Never under another user's session, a moved fence, offline or a session
  // that is not usable (C4 inv. 4): not sent, held for the session.
  const token = r.owner_key === ctx.ownerKey ? await ownerBearer(ctx, r) : null
  if (!token) return { exchange: { kind: 'answered', status: 401 }, body: null }
  // Re-checked after the last await, right before the request leaves.
  if (!liveFor(ctx, r)) return { exchange: { kind: 'answered', status: 401 }, body: null }
  try {
    const res = await a.apiFetch(a.apiUrl(r.destination.path), {
      method: r.destination.method,
      headers: { 'Content-Type': r.request.content_type, Authorization: `Bearer ${token}` },
      body: requestText(r),
    })
    return answeredFrom(res)
  } catch {
    return { exchange: exchangeForThrow(onLine()), body: null }
  }
}

function deckTransport(ctx: WriterCtx): DeckTransport | null {
  const a = adapter!
  const rest = a.supabaseRest()
  if (!rest) return null
  const headersFor = async (owner: string | null, r: OpRecord | null): Promise<Record<string, string> | null> => {
    if (owner !== ctx.ownerKey) return null
    const token = await ownerBearer(ctx, r)
    return token ? { apikey: rest.anonKey, Authorization: `Bearer ${token}`, Accept: 'application/json' } : null
  }
  return {
    readRow: async (deckId, userId) => {
      const h = await headersFor(accountOwner(userId), null)
      if (!h) return { ok: false, exchange: { kind: 'answered', status: 401 } }
      // Re-checked after the last await, right before the request leaves (r2 #9).
      if (!liveFor(ctx, null)) return { ok: false, exchange: { kind: 'answered', status: 401 } }
      try {
        const res = await a.supabaseFetch(`${rest.url}/rest/v1/saved_decks?select=*&id=eq.${encodeURIComponent(deckId)}&user_id=eq.${encodeURIComponent(userId)}&limit=1`, { headers: h })
        if (!res.ok) return { ok: false, exchange: (await answeredFrom(res)).exchange }
        const rows = (await res.json()) as unknown
        return { ok: true, row: Array.isArray(rows) && rows.length ? (rows[0] as Record<string, unknown>) : null }
      } catch {
        return { ok: false, exchange: exchangeForThrow(onLine()) }
      }
    },
    patch: async (r) => {
      const h = await headersFor(r.owner_key, r)
      if (!h) return { exchange: { kind: 'answered', status: 401 }, body: null }
      // Re-checked after the last await, right before the request leaves (r2 #9).
      if (!liveFor(ctx, r)) return { exchange: { kind: 'answered', status: 401 }, body: null }
      try {
        const res = await a.supabaseFetch(`${rest.url}${r.destination.path}`, {
          method: r.destination.method,
          headers: { ...h, 'Content-Type': r.request.content_type, ...(r.destination.headers ?? {}) },
          body: requestText(r),
        })
        const out = await answeredFrom(res)
        return { exchange: { ...out.exchange, routeFamily: null } as HttpExchange, body: out.body }
      } catch {
        return { exchange: exchangeForThrow(onLine()), body: null }
      }
    },
  }
}

// ---- Scheduler steps -------------------------------------------------------------------------------

const collectionListeners = new Set<(e: { collectionId: string; reconciliation: Reconciliation | null; record: OpRecord }) => void>()

/** A queued collection add settled (receipts for temp rows; refetch the snapshot). */
export function onCollectionAddSettled(fn: (e: { collectionId: string; reconciliation: Reconciliation | null; record: OpRecord }) => void): () => void {
  collectionListeners.add(fn)
  return () => { collectionListeners.delete(fn) }
}

async function drainCollection(): Promise<void> {
  const w = beginWriter()
  if (!w) return
  try {
    const ctx = w.ctx
    const sent = await drainCollectionQueue({
      ctx,
      current: () => currentNow(ctx),
      send: (r) => sendApiRecord(ctx, r),
      now: () => new Date(),
      u: () => Math.random(),
      signal: w.signal,
      onSettled: (record, _s, reconciliation) => {
        snapshotsDue = { ...snapshotsDue, collection: true }
        for (const l of [...collectionListeners]) l({ collectionId: record.scope.id, reconciliation, record })
      },
    })
    if (sent > 0) lastCollectionRefresh = 0
  } finally {
    w.end()
    void refreshPendingCounts()
  }
}

async function drainDecks(): Promise<void> {
  const w = beginWriter()
  const t = w && adapter ? deckTransport(w.ctx) : null
  if (!w || !t) { w?.end(); return }
  try {
    const ctx = w.ctx
    await drainDeckEdits({
      ctx,
      current: () => currentNow(ctx),
      transport: t,
      now: () => new Date(),
      u: () => Math.random(),
      signal: w.signal,
    })
  } finally {
    w.end()
    void refreshPendingCounts()
  }
}

/** Refresh the A1 snapshot: on load, after our own writes, on reconnect, when the page asks; at most once a minute otherwise. */
async function refreshCollection(): Promise<void> {
  const a = adapter!
  // An open collection screen (or a load that entered on /collection) is
  // reading its first page: wait (still due; release asks again).
  if (snapshotHolds > 0) return
  const w = beginWriter()
  if (!w) return
  try {
    const subject = ownerSubject(w.ctx.ownerKey)
    const collectionId = subject ? a.collectionIdFor(subject) : null
    if (!collectionId) { snapshotsDue = { ...snapshotsDue, collection: false }; return }
    if (!snapshotsDue.collection && Date.now() - lastCollectionRefresh < 60 * 60 * 1000) return
    if (Date.now() - lastCollectionRefresh < 60_000 && lastCollectionRefresh !== 0) return
    const ident = await a.sessionIdentity()
    if (!ident?.token || !ident.userId || accountOwner(ident.userId) !== w.ctx.ownerKey) return
    // A hold taken while the session was read: back off before any network
    // or heavy work (still due; the release asks again).
    if (snapshotHolds > 0) return
    lastCollectionRefresh = Date.now()
    const res = await refreshCollectionSnapshot({
      ctx: w.ctx, collectionId, token: ident.token,
      url: a.apiUrl(`/collections/${encodeURIComponent(collectionId)}/snapshot`),
      fetch: (u, init) => a.apiFetch(u, init),
      // The screen's sort (what a cold open asks for) and the default.
      firstPageSorts: [readSort(), DEFAULT_SORT],
      // Before each heavy step (reading the stored body, parsing the new
      // one, building the pages): wait out any hold taken since, then idle.
      yieldToIdle: async () => {
        await whenUnheld()
        await idle()
        await whenUnheld()
      },
      whenUnheld,
      isHeld: () => snapshotHolds > 0,
    })
    if (res.result !== 'transient' && res.result !== 'needs_session') snapshotsDue = { ...snapshotsDue, collection: false }
    for (const l of [...snapshotListeners]) l()
  } finally {
    w.end()
  }
}

const snapshotListeners = new Set<() => void>()

/** The stored snapshots changed (the C6 panel and the offline views re-read). */
export function onSnapshotsChanged(fn: () => void): () => void {
  snapshotListeners.add(fn)
  return () => { snapshotListeners.delete(fn) }
}

/** The collection screen opened or saved: refresh its snapshot on the next run. */
export function requestCollectionSnapshot(): void {
  snapshotsDue = { ...snapshotsDue, collection: true }
  requestSync(TRIGGER.enqueued)
}

/** Holds on the snapshot refresh (their first page goes first). */
let snapshotHolds = 0
const unheldWaiters = new Set<() => void>()
/** A hold never outlives this, whatever the screen does. */
export const SNAPSHOT_HOLD_MAX_MS = 15_000

function takeHold(): () => void {
  snapshotHolds++
  let released = false
  const release = () => {
    if (released) return
    released = true
    clearTimeout(timer)
    snapshotHolds = Math.max(0, snapshotHolds - 1)
    if (snapshotHolds > 0) return
    for (const r of [...unheldWaiters]) r()
    unheldWaiters.clear()
    if (snapshotsDue.collection) requestSync(TRIGGER.enqueued)
  }
  const timer = setTimeout(release, SNAPSHOT_HOLD_MAX_MS)
  return release
}

/** Resolves once no hold is taken (at once when none; holds are capped, so this is bounded). */
function whenUnheld(): Promise<void> {
  if (snapshotHolds === 0) return Promise.resolve()
  return new Promise(resolve => { unheldWaiters.add(resolve) })
}

/**
 * The collection screen is reading its first live page: the A1 snapshot
 * refresh (a multi-MB download, parse and write) waits until it settles.
 * Returns the release (idempotent; also released after SNAPSHOT_HOLD_MAX_MS);
 * a refresh that came due while held runs on release.
 */
export function holdCollectionSnapshot(): () => void {
  if (!offlineRuntimeActive()) return () => {}
  return takeHold()
}

/** The hold a load that ENTERED on /collection takes before the startup sync can dispatch. */
let entryHoldRelease: (() => void) | null = null

/** True when this load entered on the collection screen (the entry hold applies). */
function enteredOnCollection(): boolean {
  try {
    return typeof window !== 'undefined' && /^\/collection(\/|$)/.test(window.location.pathname)
  } catch {
    return false
  }
}

/**
 * The page took its own hold (or settled, or is not the per-copy list):
 * the entry hold goes. Idempotent; it also goes by itself after
 * SNAPSHOT_HOLD_MAX_MS.
 */
export function releaseCollectionEntryHold(): void {
  const r = entryHoldRelease
  entryHoldRelease = null
  r?.()
}

/** Waits for the browser to be idle (bounded), else a macrotask. */
function idle(): Promise<void> {
  return new Promise((resolve) => {
    const ric = typeof window !== 'undefined'
      ? (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
      : undefined
    if (typeof ric === 'function') ric.call(window, () => resolve(), { timeout: 2000 })
    else setTimeout(resolve, 0)
  })
}

/**
 * The facts a provisional page is bound to, now: the owner's fence
 * (`owner|incarnation|epoch`) when the session owner `userId` IS
 * offline_owner, its store is open (not suspended, no transition in the C3
 * mirror, no recovery block) and `collectionId` is its stored pointer; else
 * null.
 */
function provisionalFacts(userId: string, collectionId: string): FirstPageFence | null {
  if (!offlineRuntimeActive() || !adapter || blocked) return null
  const owner = accountOwner(userId)
  if (getIdentity().offlineOwner !== owner) return null
  const ctx = currentWriterCtx()
  const m = readFenceMirror()
  if (!ctx || ctx.ownerKey !== owner || !m || m.transition_id !== null || m.owner_key !== owner || m.fence_epoch !== ctx.fenceEpoch) return null
  if (adapter.collectionIdFor(userId) !== collectionId) return null
  return { ownerKey: owner, incarnation: ctx.incarnation, fenceEpoch: ctx.fenceEpoch }
}

function sameFence(a: FirstPageFence | null, b: FirstPageFence | null): boolean {
  return !!a && !!b && a.ownerKey === b.ownerKey && a.incarnation === b.incarnation && a.fenceEpoch === b.fenceEpoch
}

/**
 * A provisional page read earlier may still be shown: its fence is the
 * current one and every fact above still holds. Read on every change
 * `subscribeProvisionalFacts` reports; false drops the page for good.
 */
export function provisionalPageValid(page: ProvisionalFirstPage, userId: string): boolean {
  return sameFence(provisionalFacts(userId, page.collectionId), page.fence)
}

/**
 * Every change that can invalidate a provisional page: this tab's owner
 * context (suspension, a new incarnation or epoch, a cleared owner), the
 * recovery block, identity, and another tab's fence mirror write.
 */
export function subscribeProvisionalFacts(fn: () => void): () => void {
  const offs = [subscribeOfflineContext(fn), subscribeRecoveryBlocked(fn), subscribeIdentity(fn)]
  const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === FENCE_MIRROR_KEY) fn() }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  return () => {
    for (const o of offs) o()
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

/**
 * The provisional first page of the Cards list for (sort, dir), read from
 * this device's owner store, or null. Only for the signed-in session owner
 * (`userId`) when it IS offline_owner, its store is open (no transition, no
 * recovery block, the C3 mirror agrees) and `collectionId` is the owner's
 * stored collection pointer; the same facts are captured before the read and
 * re-checked after it, and the record must have been written under exactly
 * the current fence (owner, incarnation, epoch). The page carries that fence:
 * the caller drops it the moment `provisionalPageValid` says no. Painting
 * only: never writable.
 */
export async function readProvisionalFirstPage(args: {
  userId: string
  collectionId: string
  sort: CopySort
  dir: SortDir
}): Promise<ProvisionalFirstPage | null> {
  if (!offlineRuntimeActive()) return null
  await offlineReady()
  const before = provisionalFacts(args.userId, args.collectionId)
  if (!before) return null
  let rec
  try {
    rec = await readFirstPageRecord(before.ownerKey, args.collectionId, before)
  } catch {
    return null
  }
  if (!rec || !sameFence(provisionalFacts(args.userId, args.collectionId), before)) return null
  return firstPageFor(rec, { sort: args.sort, dir: args.dir })
}

const CARD_REFRESH_MS = 7 * 24 * 60 * 60 * 1000
const CARD_CHUNK = 120
const CARD_BATCH_PER_RUN = 600

/** Card metadata for every card of every stored deck: missing names, and everything weekly. */
async function refreshDeckCardsStep(): Promise<void> {
  const a = adapter!
  const w = beginWriter()
  if (!w) return
  try {
    const snap = await readDeckSnapshot(w.ctx.ownerKey)
    if (!snap) {
      // Nothing stored yet: the next deck snapshot makes this due again.
      snapshotsDue = { ...snapshotsDue, deckCards: false }
      return
    }
    const have = await readDeckCardAges(w.ctx.ownerKey)
    const names = new Set<string>()
    for (const d of snap.decks) {
      for (const n of a.cardNamesOf(d.decklist ?? '')) names.add(n)
      if (d.commander) names.add(d.commander)
      if (d.partner) names.add(d.partner)
    }
    // Missing names first, then entries older than a week (each refreshed
    // entry gets a new date, so capped runs converge); entries no stored deck
    // needs any more are pruned.
    const required = new Set([...names].map(cardKey))
    const now = Date.now()
    const missingNames = [...names].filter(n => !have.has(cardKey(n)))
    const agedNames = [...names].filter((n) => {
      const e = have.get(cardKey(n))
      return !!e && (!e.fetched_at || now - Date.parse(e.fetched_at) > CARD_REFRESH_MS)
    })
    const want = [...new Set([...missingNames, ...agedNames])]
    const prune = [...have.entries()].filter(([, e]) => !cardKeysOf(e.info).some(k => required.has(k))).map(([k]) => k)
    if (want.length === 0) {
      if (prune.length) await writeDeckCards(w.ctx, [], null, prune)
      snapshotsDue = { ...snapshotsDue, deckCards: false }
      if (prune.length) await loadOfflineCardSource()
      return
    }
    // Sequential, at most CARD_BATCH_PER_RUN names a run (/cards/images is
    // rate limited); the rest stays due for the next run.
    const batch = want.slice(0, CARD_BATCH_PER_RUN)
    const infos: CardImage[] = []
    for (let i = 0; i < batch.length; i += CARD_CHUNK) {
      if (w.signal.aborted) return
      infos.push(...await a.fetchCardInfo(batch.slice(i, i + CARD_CHUNK)))
    }
    if (w.signal.aborted) return
    const resolved = new Set<string>()
    for (const i of infos) {
      resolved.add(cardKey(i.name))
      if (i.name.includes(' // ')) resolved.add(cardKey(i.name.split(' // ')[0]))
      for (const f of i.faces ?? []) resolved.add(cardKey(f.name))
    }
    const missing = [...names].filter(n => !have.has(cardKey(n)) && !resolved.has(cardKey(n))).length
    await writeDeckCards(w.ctx, infos, want.length > batch.length ? null : { missing }, prune)
    snapshotsDue = { ...snapshotsDue, deckCards: want.length > batch.length }
    await loadOfflineCardSource()
    for (const l of [...snapshotListeners]) l()
  } finally {
    w.end()
  }
}

/** Install the stored deck card metadata as the offline answer for /cards/images. */
async function loadOfflineCardSource(): Promise<void> {
  const ctx = knownWriterCtx()
  if (!ctx) {
    setOfflineCardSource(null)
    return
  }
  const map = await readDeckCards(ctx.ownerKey)
  setOfflineCardSource({ lookup: k => map.get(k), preferOffline: () => connectivityState() !== CONN.online })
}

// ---- Public writes (the UI) ---------------------------------------------------------------------------

/**
 * The decks list loaded completely from the cloud (or one row landed): store
 * it for offline_owner. Only for the owner's own rows (`userId` is the
 * account the rows belong to).
 */
export async function storeDeckSnapshot(userId: string, decks: SavedDeck[], opts: { complete: boolean }): Promise<void> {
  if (!offlineRuntimeActive()) return
  const w = beginWriter()
  if (!w) return
  try {
    if (w.ctx.ownerKey !== accountOwner(userId)) return
    const res = await writeDeckSnapshot(w.ctx, decks, opts)
    if (res === 'ok') {
      snapshotsDue = { ...snapshotsDue, deckCards: true }
      for (const l of [...snapshotListeners]) l()
      requestSync(TRIGGER.enqueued)
    }
  } catch {
    // A quota error is reported by the C6 panel (it reads the status).
  } finally {
    w.end()
  }
}

/** The stored decks of offline_owner (offline reads), or null. */
export async function readOwnerDecks(userId: string): Promise<{ decks: SavedDeck[]; refreshedAt: string | null } | null> {
  if (!offlineRuntimeActive()) return null
  await offlineReady()
  const owner = accountOwner(userId)
  if (getIdentity().offlineOwner !== owner) return null
  return readDeckSnapshot(owner)
}

/** The refused reasons of an offline write. */
export type OfflineWriteResult = 'queued' | 'cleared' | 'read_only' | 'unavailable' | 'refused'

/** C2 permission, re-checked INSIDE the committing transaction (finding 2). */
function mayQueue(): boolean {
  const id = getIdentity()
  return !blocked && id.allowed.queue && id.allowed.edit
}

/**
 * Record a deck edit for the outbox (offline, refresh pending, or while an
 * attempt for the deck is unsettled). Refused in `rejected` (read-only).
 * 'cleared': the edit brought the deck back to its acknowledged state and
 * nothing is pending for it any more.
 */
export async function recordOfflineDeckEdit(input: EditInput): Promise<OfflineWriteResult> {
  if (!offlineRuntimeActive()) return 'unavailable'
  if (!mayQueue()) return 'read_only'
  const w = beginWriter()
  if (!w) return 'unavailable'
  try {
    const res = await recordDeckEdit(w.ctx, input, new Date(), mayQueue)
    if (res !== 'ok') return mayQueue() ? 'refused' : 'read_only'
    await refreshPendingCounts()
    requestSync(TRIGGER.enqueued)
    return deckHasOfflineWorkSync(input.deckId) ? 'queued' : 'cleared'
  } finally {
    w.end()
  }
}

/** Whether the outbox holds a draft or an unsettled attempt for the deck (one writer per deck). */
export async function deckHasOfflineWork(deckId: string): Promise<boolean> {
  if (!offlineRuntimeActive()) return false
  const ctx = knownWriterCtx()
  if (!ctx) return false
  const drafts = await readDrafts(ctx.ownerKey)
  if (drafts.some(d => d.deck_id === deckId)) return true
  return (await readQueue(ctx.ownerKey)).some(r => r.kind === 'deck_edit' && r.scope.id === deckId && isOpen(r))
}

/**
 * Queue a collection add (frozen now: chunks of 250, op_key <base>-c<i>).
 * Durable before anything is sent; drained by the scheduler.
 */
export async function enqueueCollectionAdd(collectionId: string, items: AddItemWire[], source?: string): Promise<{ result: OfflineWriteResult; records?: OpRecord[] }> {
  if (!offlineRuntimeActive()) return { result: 'unavailable' }
  if (!mayQueue()) return { result: 'read_only' }
  const w = beginWriter()
  if (!w) return { result: 'unavailable' }
  try {
    const records = buildCollectionAddRecords({ ownerKey: w.ctx.ownerKey, incarnation: w.ctx.incarnation, collectionId, items, source, now: new Date() })
    const res = await putRecords(w.ctx, records, mayQueue)
    if (res !== 'ok') return { result: mayQueue() ? 'refused' : 'read_only' }
    await refreshPendingCounts()
    requestSync(TRIGGER.enqueued)
    return { result: 'queued', records }
  } finally {
    w.end()
  }
}

export type ReviewAction = 'send' | 'discard' | 'add_again_as_new' | 'retry_failed'

/**
 * The user's answer to a record waiting for review (C4.5): Send (below 29
 * days), Discard, Add again as new (a new group), Try the failed cards again
 * (a new group of only the failed inputs). ATOMIC (finding 8): the original
 * is re-read, validated and consumed in the SAME fenced transaction that
 * writes its replacement, so of two tabs (or two clicks) only one succeeds.
 */
export async function resolveQueueReview(opId: string, action: ReviewAction): Promise<OfflineWriteResult> {
  if (!offlineRuntimeActive()) return 'unavailable'
  if (!mayQueue()) return 'read_only'
  const w = beginWriter()
  if (!w) return 'unavailable'
  const ctx = w.ctx
  try {
    let applied = false
    const res = await fencedWrite(ctx, [STORE.queue], tx => {
      const st = tx.objectStore(STORE.queue)
      const g = st.get(opId)
      g.onsuccess = () => {
        const r = g.result as OpRecord | undefined
        if (!r || r.owner_key !== ctx.ownerKey) return
        const now = new Date()
        switch (action) {
          case 'send':
            if (!reviewOptions(r, now.getTime()).includes('send')) return
            st.put(approveSend(r), r.op_id)
            break
          case 'discard':
            st.delete(r.op_id)
            break
          case 'add_again_as_new':
            for (const x of addAgainAsNew(r, ctx, now)) st.put(x, x.op_id)
            st.delete(r.op_id)
            break
          case 'retry_failed': {
            const next = retryFailedAsNew(r, ctx, now)
            if (next.length === 0) return
            for (const x of next) st.put(x, x.op_id)
            st.delete(r.op_id)
            break
          }
        }
        applied = true
      }
    }, mayQueue)
    if (res !== 'ok' || !applied) return 'refused'
    await refreshPendingCounts()
    requestSync(TRIGGER.enqueued)
    return 'queued'
  } finally {
    w.end()
  }
}

export type DeckReviewAction = 'keep_mine' | 'discard' | 'try_again'

/**
 * The user's explicit answer for a deck the outbox stopped on (finding 3,
 * 7): a conflict (keep mine: rebased on the row the conflict was found
 * against; discard), a missing row (discard), a permanent failure (try
 * again: the failed attempt goes and the draft runs again; discard).
 * Nothing of the draft is ever removed without one of these.
 */
export async function resolveDeckReview(deckId: string, action: DeckReviewAction): Promise<OfflineWriteResult> {
  if (!offlineRuntimeActive()) return 'unavailable'
  if (action !== 'discard' && !mayQueue()) return 'read_only'
  const w = beginWriter()
  if (!w) return 'unavailable'
  try {
    const res = action === 'discard'
      ? await discardDeckDraft(w.ctx, deckId)
      : action === 'keep_mine'
        ? await keepMineDeckDraft(w.ctx, deckId, new Date(), mayQueue)
        : await retryDeckDraft(w.ctx, deckId, mayQueue)
    if (res !== 'ok') return 'refused'
    await refreshPendingCounts()
    requestSync(TRIGGER.enqueued)
    return 'queued'
  } finally {
    w.end()
  }
}

/** The owner's deck drafts that wait for the user (the review panel). */
export async function readStoppedDeckDrafts(): Promise<DeckDraft[]> {
  const ctx = knownWriterCtx()
  if (!ctx || !active) return []
  return (await readDrafts(ctx.ownerKey)).filter(d => !!d.status)
}

/** The owner's queued collection add records (the offline collection view's pending rows). */
export async function readQueuedAdds(): Promise<OpRecord[]> {
  const ctx = knownWriterCtx()
  if (!ctx || !active) return []
  return (await readQueue(ctx.ownerKey)).filter(r => r.kind === 'collection_add')
}

export function offlineOwnerKey(): string {
  return getIdentity().offlineOwner ?? GUEST_OWNER
}

export { META_DECKS, SCHEMA_VERSION, rfc3339 }

/** Test seam. */
export function __resetOfflineRuntimeForTests(): void {
  for (const t of teardown.splice(0)) t()
  adapter = null
  bootPromise = null
  active = false
  transitionInFlight = false
  pending = { unsynced: 0, attention: 0, queuedAdds: 0, deckDrafts: 0 }
  decksWithOfflineWork = new Set()
  snapshotsDue = { decks: false, deckCards: true, collection: true }
  lastCollectionRefresh = 0
  snapshotHolds = 0
  unheldWaiters.clear()
  entryHoldRelease = null
  pendingListeners.clear()
  snapshotListeners.clear()
  collectionListeners.clear()
  blocked = false
  blockedListeners.clear()
  if (recoveryTimer) clearInterval(recoveryTimer)
  recoveryTimer = null
  recovering = null
}
